import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const CartContext = createContext(null);

export const CartProvider = ({ children }) => {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [wishlist, setWishlist] = useState([]);

  const refreshCart = useCallback(async () => {
    if (!user) {
      setItems([]);
      return;
    }
    try {
      const { data } = await api.get("/cart");
      setItems(data.items || []);
    } catch (e) {
      console.error(e);
    }
  }, [user]);

  const refreshWishlist = useCallback(async () => {
    if (!user) {
      setWishlist([]);
      return;
    }
    try {
      const { data } = await api.get("/wishlist");
      setWishlist(data.items || []);
    } catch (e) {
      console.error(e);
    }
  }, [user]);

  useEffect(() => {
    refreshCart();
    refreshWishlist();
  }, [refreshCart, refreshWishlist]);

  const addToCart = async (product_id, quantity = 1, variant_sku = null) => {
    if (!user) {
      toast.error("Please login to add items to your cart");
      return false;
    }
    try {
      await api.post("/cart/add", { product_id, quantity, variant_sku });
      await refreshCart();
      toast.success("Added to cart");
      return true;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not add to cart");
      return false;
    }
  };

  const updateQuantity = async (product_id, quantity, variant_sku = null) => {
    await api.post("/cart/update", { product_id, quantity, variant_sku });
    await refreshCart();
  };

  const removeFromCart = async (product_id, variant_sku = null) => {
    await api.post("/cart/remove", { product_id, variant_sku });
    await refreshCart();
    toast.success("Removed from cart");
  };

  const toggleWishlist = async (product_id) => {
    if (!user) {
      toast.error("Please login to use wishlist");
      return;
    }
    const { data } = await api.post(`/wishlist/toggle/${product_id}`);
    await refreshWishlist();
    toast.success(data.added ? "Added to wishlist" : "Removed from wishlist");
  };

  const isInWishlist = (product_id) =>
    wishlist.some((p) => p.id === product_id);

  const cartTotal = items.reduce(
    (sum, i) => sum + (
      i.checkout_eligible === true
        ? Number(i.product?.price || 0) * i.quantity
        : 0
    ),
    0
  );

  const cartCount = items.reduce((sum, i) => sum + i.quantity, 0);

  return (
    <CartContext.Provider
      value={{
        items,
        wishlist,
        cartTotal,
        cartCount,
        addToCart,
        updateQuantity,
        removeFromCart,
        toggleWishlist,
        isInWishlist,
        refreshCart,
        refreshWishlist,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);
